import { Suspense, lazy } from 'react';
import type { PartialRouteObject } from 'react-router';
import { AuthGuard } from './components/auth-guard';
import { GuestGuard } from './components/guest-guard';
import { RoleBasedGuard } from './components/role-based-guard';
import { LoadingScreen } from './components/loading-screen';
import { Account } from './containers/account';
import { DashboardLayout } from './containers/dashboard-layout';
import { ComingSoon } from './containers/coming-soon';
import { Roles } from './enums/role-based-auth-guard';
import { ChannelPartnerApprove } from './components/new-channel-partner/addChannelPartner/approveChannelPartner';
import { ApproveTechnician } from './components/new-technician/addTechnician/approveTechnician';
import { CustomersManagement } from './containers/customer-management';
import { DeleteCustomersAccount } from './containers/customer-delete-request';
import { RoleNActiveStatBasedGuard } from './components/role-n-activeStat-based-guard';

const Loadable = (Component) => (props) => (
  <Suspense fallback={<LoadingScreen />}>
    <Component {...props} />
  </Suspense>
);

// Not found pages
const NotFound = Loadable(lazy(() => import('./containers/not-found').then((module) => ({ default: module.NotFound }))));

// Auth pages
const Login = Loadable(lazy(
  () => import('./containers/login').then((module) => ({ default: module.Login }))
));
const PasswordRecovery = Loadable(lazy(
  () => import('./containers/password-recovery').then((module) => ({ default: module.PasswordRecovery }))
));
const PasswordReset = Loadable(lazy(
  () => import('./containers/password-reset').then((module) => ({ default: module.PasswordReset }))
));
const Register = Loadable(lazy(
  () => import('./containers/register').then((module) => ({ default: module.Register }))
));
const VerifyCode = Loadable(lazy(
  () => import('./containers/verify-code').then((module) => ({ default: module.VerifyCode }))
));
const FirstLogin = Loadable(lazy(
  () => import('./containers/first-login').then((module) => ({ default: module.FirstLogin }))
));
const VerifyLinkPayment = Loadable(lazy(
  () => import('./containers/verify-link-payment').then((module) => ({ default: module.VerifyLinkPayment }))
));
// Master pages
const Masters = Loadable(lazy(
  () => import('./containers/masters').then((module) => ({ default: module.Masters }))
));

// Base Masters
const BaseMasters = Loadable(lazy(
  () => import('./containers/masters-base').then((module) => ({ default: module.BaseMasters }))
));

const Category = Loadable(lazy(
  () => import('./containers/masters/base/category').then((module) => ({ default: module.Category }))
));
const Product = Loadable(lazy(
  () => import('./containers/masters/base/product').then((module) => ({ default: module.Product }))
));
const Brand = Loadable(lazy(
  () => import('./containers/masters/base/brand').then((module) => ({ default: module.Brand }))
));

// Model Master
const ModelMaster = Loadable(lazy(
  () => import('./containers/masters-model').then((module) => ({ default: module.ModelMasters }))
));

const AddModel = Loadable(lazy(
  () => import('./components/masters/modelMaster/add-model').then((module) => ({ default: module.AddNewModel }))
));

const ModelView = Loadable(lazy(
  () => import('./components/masters/modelMaster/model-view').then((module) => ({ default: module.ModelView }))
));

const EditModel = Loadable(lazy(
  () => import('./components/masters/modelMaster/edit-model').then((module) => ({ default: module.EditModel }))
));

// SparePart Master
const SpartPartMaster = Loadable(lazy(
  () => import('./containers/masters-spare-part').then((module) => ({ default: module.SparePartMaster }))
));

const SpartPartMasterAll = Loadable(lazy(
  () => import('./containers/masters/sparePart/spare-part-all').then((module) => ({ default: module.SparePart }))
));

const GenericSpareParts = Loadable(lazy(
  () => import('./containers/masters-genericSpareParts').then((module) => ({ default: module.GenericSpareParts }))
));

const SparePartFind = Loadable(lazy(
  () => import('./containers/masters/sparePart/find-by-model-id').then((module) => ({ default: module.SparePartFind }))
));

const AddSparePart = Loadable(lazy(
  () => import('./containers/masters/sparePart/add-spare-part').then((module) => ({ default: module.AddSparePart }))
));

const AddNewSparePart = Loadable(lazy(
  () => import('./components/masters/sparePart/add-spare-part').then((module) => ({ default: module.AddSparePartAdd }))
));

const SparePartAssign = Loadable(lazy(
  () => import('./components/masters/sparePart/spare-part-assign').then((module) => ({ default: module.SparePartAssign }))
));

const AddGenericSparePart = Loadable(lazy(
  () => import('./components/masters/sparePart/genericSparePart/add-generic-spare-part').then((module) => ({ default: module.AddGenericSparePart }))
));

const SparePartView = Loadable(lazy(
  () => import('./components/masters/sparePart/spare-part-view').then((module) => ({ default: module.SparePartView }))
));

const GenericSparePartView = Loadable(lazy(
  () => import('./components/masters/sparePart/genericSparePart/generic-spare-part-view').then((module) => ({ default: module.GenericSparePartView }))
));

const SparePartEdit = Loadable(lazy(
  () => import('./components/masters/sparePart/spare-part-edit').then((module) => ({ default: module.SparePartEdit }))
));

const GenericSparePartEdit = Loadable(lazy(
  () => import('./components/masters/sparePart/genericSparePart/generic-spare-part-edit').then((module) => ({ default: module.GenericSparePartEdit }))
));

const SparePartModelView = Loadable(lazy(
  () => import('./components/masters/sparePart/spart-part-modelView').then((module) => ({ default: module.SparePartModelView }))
));

// Price Master
const PriceMaster = Loadable(lazy(
  () => import('./containers/masters-price').then((module) => ({ default: module.PriceMasters }))
));

const AddPrice = Loadable(lazy(
  () => import('./containers/masters/price/add-price').then((module) => ({ default: module.AddPrice }))
));

// Price Service
const PriceService = Loadable(lazy(
  () => import('./containers/masters/price/servicePrice').then((module) => ({ default: module.PriceService }))
));

const AddServicePrice = Loadable(lazy(
  () => import('./containers/masters/price/servicePrice-add').then((module) => ({ default: module.AddServicePrice }))
));

const PriceServiceModelView = Loadable(lazy(
  () => import('./components/masters/price/servicePrice/priceService-modelView').then((module) => ({ default: module.PriceServiceModelView }))
));

const ServicePriceView = Loadable(lazy(
  () => import('./components/masters/price/servicePrice/servicePrice-view').then((module) => ({ default: module.ServicePriceView }))
));

// Price SparePart
const PriceSpartPart = Loadable(lazy(
  () => import('./containers/masters/price/priceSparePart').then((module) => ({ default: module.PriceSpartPart }))
));

// const PriceSparePartView = Loadable(lazy(
//   () => import('./components/masters/price/sparePart/view-spare-part-price')
// .then((module) => ({ default: module.ViewSparePartPriceDetails }))
// ));

// const PriceSparePartEdit = Loadable(lazy(
//   () => import('./components/masters/price/sparePart/edit-spare-part-price')
// .then((module) => ({ default: module.PriceSparePartEdit }))
// ));

const AddNewSparePartPrice = Loadable(lazy(
  () => import('./components/masters/price/sparePartPrice/add-spare-part-price').then((module) => ({ default: module.AddNewSparePartPrice }))
));

const ViewSparePartPriceDetails = Loadable(lazy(
  () => import('./components/masters/price/sparePartPrice/view-spare-part-price').then((module) => ({ default: module.ViewSparePartPriceDetails }))
));

// Repair Service Master
const RepairServiceMaster = Loadable(lazy(
  () => import('./containers/masters-repairService').then((module) => ({ default: module.RepairServiceMaster }))
));

const CustomerView = Loadable(lazy(
  () => import('./containers/customer/customerView').then((module) => ({ default: module.CustomerView }))
));

// E Waste Service Master
const EWasteServiceMaster = Loadable(lazy(
  () => import('./containers/masters-eWaste-Service').then((module) => ({ default: module.EWasteServiceMaster }))
));

// Dashboard
const Dashboard = Loadable(lazy(
  () => import('./containers/dashboard').then((module) => ({ default: module.Dashboard }))
));

// Book Request
const BookRequest = Loadable(lazy(
  () => import('./containers/book-request').then((module) => ({ default: module.BookRequest }))
));

// Channel Partners
const ChannelPartners = Loadable(lazy(
  () => import('./containers/channel-partners').then((module) => ({ default: module.ChannelPartners }))
));

const IncompleteChannelPartners = Loadable(lazy(
  () => import('./containers/channel-partner/incomplete-channel-partner').then((module) => ({ default: module.IncompleteChannelPartners }))
));

const InvitedChannelPartners = Loadable(lazy(
  () => import('./containers/channel-partner/invited-channel-partner').then((module) => ({ default: module.InvitedChannelPartners }))
));

const VerifiedChannelPartners = Loadable(lazy(
  () => import('./containers/channel-partner/verified-channel-partners').then((module) => ({ default: module.VerifiedChannelPartners }))
));
const ChannelPartnersManagement = Loadable(lazy(
  () => import('./containers/channel-partner-management').then((module) => ({ default: module.ChannelPartnersManagement }))
));

const ChannelPartner = Loadable(lazy(
  () => import('./containers/channel-partner/channel-partner').then((module) => ({ default: module.ChannelPartner }))
));

const VerifyChannelPartner = Loadable(lazy(
  () => import('./containers/channel-partner/verify-channel-partner').then((module) => ({ default: module.VerifyChannelPartner }))
));

const AddChannelPartner = Loadable(lazy(
  () => import('./components/channel-partners/add-channel-partner').then((module) => ({ default: module.AddChannelPartner }))
));

const ChannelPartnerProfileDetail = Loadable(lazy(
  () => import('./components/channel-partners/channel-partner-profile-details').then((module) => ({ default: module.ChannelPartnerProfileDetail }))
));

// New Channel Partner
const ChannelPartnerProfile = Loadable(lazy(
  () => import('./components/new-channel-partner/channel-partner-profile/my-profile').then((module) => ({ default: module.ChannelPartnerProfile }))
));

const ChannelPartnerAdd = Loadable(lazy(
  () => import('./components/new-channel-partner/addChannelPartner/add-channel-partner-card').then((module) => ({ default: module.ChannelPartnerAdd }))
));

const ChannelPartnerDetails = Loadable(lazy(
  () => import('./components/new-channel-partner/addChannelPartner/channelPartnerDetails').then((module) => ({ default: module.ChannelPartnerDetails }))
));

const VerifiedChannelPartnerOverview = Loadable(lazy(
  () => import('./components/new-channel-partner/verified-channel-partner/verifiedChannelPartner').then((module) => ({ default: module.VerifiedChannelPartner }))
));

// Dealers
const Dealers = Loadable(lazy(
  () => import('./containers/dealers').then((module) => ({ default: module.Dealers }))
));

const DealerDetails = Loadable(lazy(
  () => import('./components/dealers/dealer-details').then((module) => ({ default: module.DealerDetails }))
));

const VerifyDealer = Loadable(lazy(
  () => import('./containers/dealers/verify-dealer').then((module) => ({ default: module.VerifyDealer }))
));

// New Dealer
const DealerAdd = Loadable(lazy(
  () => import('./components/dealers/dealer-add').then((module) => ({ default: module.DealerAdd }))
));

const DealerProfile = Loadable(lazy(
  () => import('./components/dealers/my-profile-dealer').then((module) => ({ default: module.DealerProfile }))
));

// New Technician
const TechnicianAdd = Loadable(lazy(
  () => import('./components/new-technician/addTechnician/add-technician-card').then((module) => ({ default: module.TechnicianAdd }))
));

const TechnicianDetails = Loadable(lazy(
  () => import('./components/new-technician/addTechnician/technicianDetails').then((module) => ({ default: module.TechnicianDetails }))
));

// Technician
const Technicians = Loadable(lazy(
  () => import('./containers/technician').then((module) => ({ default: module.Technicians }))
));

const PendingTechnicians = Loadable(lazy( // eslint-disable-line
  () => import('./containers/technician/pending-technician').then((module) => ({ default: module.PendingTechnicians }))
));

const VerifiedTechnicians = Loadable(lazy( // eslint-disable-line
  () => import('./containers/technician/verified-technician').then((module) => ({ default: module.VerifiedTechnicians }))
));

const InCompleteTechnicians = Loadable(lazy( // eslint-disable-line
  () => import('./containers/technician/inComplete-technician').then((module) => ({ default: module.InCompleteTechnicians }))
));

const VerifiedTechnicianView = Loadable(lazy(
  () => import('./components/new-technician/addTechnician/verifiedTechnician').then((module) => ({ default: module.VerifiedTechnicianView }))
));

const TechniciansManagement = Loadable(lazy(
  () => import('./containers/technician-management').then((module) => ({ default: module.TechniciansManagement }))
));

const TechnicianProfileDetail = Loadable(lazy(
  () => import('./components/technicians/technician-profile-details').then((module) => ({ default: module.TechnicianProfileDetail }))
));

const AccountGeneral = Loadable(lazy(
  () => import('./containers/account-general').then((module) => ({ default: module.AccountGeneral }))
));
const AccountNotifications = Loadable(lazy(
  () => import('./containers/account-notifications').then((module) => ({ default: module.AccountNotifications }))
));

const OrganizationTeam = Loadable(lazy(
  () => import('./containers/organization-team').then((module) => ({ default: module.OrganizationTeam }))
));

const AllApkList = Loadable(lazy(
  () => import('./containers/all-apklist').then((module) => ({ default: module.AllApkList }))
));

const Services = Loadable(lazy(
  () => import('./containers/services').then((module) => ({ default: module.Services }))
));

const RepairRequestService = Loadable(lazy(
  () => import('./containers/services-repairRequest').then((module) => ({ default: module.RepairRequestService }))
));

const RepairRequestView = Loadable(lazy(
  () => import('./components/dashboard/serviceRequest/repairRequestViewNew/repairRequest-view').then((module) => ({ default: module.RepairRequestView }))
));

const RepairRequestDealerView = Loadable(lazy(
  () => import('./components/dashboard/serviceRequest/repairRequestViewOnly/repairRequest-view').then((module) => ({ default: module.RepairRequestView }))
));

const EWasteRequestView = Loadable(lazy(
  () => import('./components/dashboard/eWasteRequest/eWasteRequestView/eWasteRequest-view').then((module) => ({ default: module.EWasteRequestView }))
));

const AddModelRepairService = Loadable(lazy(
  () => import('./containers/services/addModelRepairRequestService').then((module) => ({ default: module.AddModelRepairService }))
));

// issue master containers and components
const IssueMaster = Loadable(lazy(
  () => import('./containers/masters-issue').then((module) => ({ default: module.IssueMaster }))
));

const AddAssignIssue = Loadable(lazy(
  () => import('./containers/masters/issue/add-assign-issue').then((module) => ({ default: module.AddAssignIssue }))
));

const AddIssue = Loadable(lazy(
  () => import('./components/masters/issue/issue-add').then((module) => ({ default: module.AddIssue }))
));

const AssignIssue = Loadable(lazy(
  () => import('./components/masters/issue/issue-assign').then((module) => ({ default: module.AssignIssue }))
));

const ViewIssue = Loadable(lazy(
  () => import('./components/masters/issue/issue-view').then((module) => ({ default: module.ViewIssue }))
));

const EditIssue = Loadable(lazy(
  () => import('./components/masters/issue/issue-edit').then((module) => ({ default: module.EditIssue }))
));

const EditProductIssue = Loadable(lazy(
  () => import('./components/masters/issue/product-edit').then((module) => ({ default: module.EditProductIssue }))
));

const ViewProductIssue = Loadable(lazy(
  () => import('./components/masters/issue/product-view').then((module) => ({ default: module.ViewProductIssue }))
));

// general master route components
const GeneralMaster = Loadable(lazy(
  () => import('./containers/masters-general').then((module) => ({ default: module.GeneralMaster }))
));

const ServiceType = Loadable(lazy(
  () => import('./containers/masters/general/serviceType').then((module) => ({ default: module.ServiceType }))
));

const GST = Loadable(lazy(
  () => import('./containers/masters/general/gst').then((module) => ({ default: module.GST }))
));

const UnitOfMeasurement = Loadable(lazy(
  () => import('./containers/masters/general/uom').then((module) => ({ default: module.UnitOfMeasurement }))
));

const Pincode = Loadable(lazy(
  () => import('./containers/masters/general/pincode').then((module) => ({ default: module.PinCode }))
));

const RewardPoint = Loadable(lazy(
  () => import('./components/masters/general/rewardPoints/rewardPoints-view').then((module) => ({ default: module.RewardPoint }))
));

// payments section
const Payments = Loadable(lazy(
  () => import('./containers/payments').then((module) => ({ default: module.Payments }))
));

// escalations section
const Escalations = Loadable(lazy(
  () => import('./containers/escalations').then((module) => ({ default: module.Escalations }))
));

const EscalationView = Loadable(lazy(
  () => import('./components/escalations/escalation/escalation-view').then((module) => ({ default: module.EscalationView }))
));

const EnquiryView = Loadable(lazy(
  () => import('./components/escalations/enquiry/enquiry-view').then((module) => ({ default: module.EnquiryView }))
));

// settings
const Settings = Loadable(lazy(
  () => import('./containers/settings').then((module) => ({ default: module.Settings }))
));

// about us
const AboutUs = Loadable(lazy(
  () => import('./containers/about-us').then((module) => ({ default: module.AboutUs }))
));

// Terms and Conditions
const TermsConditions = Loadable(lazy(
  () => import('./containers/terms-conditions').then((module) => ({ default: module.TermsConditions }))
));

// Privacy Policy
const PrivacyPolicy = Loadable(lazy(
  () => import('./containers/privacy-policy').then((module) => ({ default: module.PrivacyPolicy }))
));

// Reward Point section
const RewardPoints = Loadable(lazy(
  () => import('./containers/rewardPoints').then((module) => ({ default: module.RewardPoints }))
));

// Website
const Statistics = Loadable(lazy(
  () => import('./components/website/statistics').then((module) => ({ default: module.Statistics }))
));

const EwasteDrives = Loadable(lazy(
  () => import('./components/website/ewaste-drives').then((module) => ({ default: module.EwasteDrives }))
));
const routes: PartialRouteObject[] = [
  {
    path: '/',
    element: (
      <GuestGuard>
        <Login />
      </GuestGuard>
    )
  },
  {
    path: 'login',
    element: (
      <GuestGuard>
        <Login />
      </GuestGuard>
    )
  },
  {
    path: 'register',
    element: (
      <GuestGuard>
        <Register />
      </GuestGuard>
    )
  },
  {
    path: 'verify-code',
    element: (
      <GuestGuard>
        <VerifyCode />
      </GuestGuard>
    )
  },
  {
    path: 'verify-link-payment',
    element: (
      <GuestGuard>
        <VerifyLinkPayment />
      </GuestGuard>
    )
  },
  {
    path: 'password-recovery',
    element: (
      <GuestGuard>
        <PasswordRecovery />
      </GuestGuard>
    )
  },
  {
    path: 'password-reset',
    element: (
      <PasswordReset />
    )
  },
  {
    path: 'first-login',
    element: (
      <GuestGuard>
        <FirstLogin />
      </GuestGuard>
    )
  },
  {
    path: 'terms',
    element: (
      <TermsConditions />
    )
  },
  {
    path: 'privacy',
    element: (
      <PrivacyPolicy />
    )
  },
  {
    path: 'verify-channel-partner',
    children: [
      {
        path: ':authChallengeResponse',
        element: (
          <GuestGuard>
            <VerifyChannelPartner />
          </GuestGuard>
        )
      }
    ]
  },
  {
    path: 'verify-dealer',
    children: [
      {
        path: ':authChallengeResponse',
        element: (
          <GuestGuard>
            <VerifyDealer />
          </GuestGuard>
        )
      }
    ]
  },
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: '/',
        element: (
          <RoleBasedGuard roles={[
            Roles.WEB_ADMIN,
            Roles.CHANNEL_PARTNER,
            Roles.TECHNICIAN,
            Roles.DEALER]}
          >
            <Dashboard />
          </RoleBasedGuard>
        )
      },
      {
        path: '/my-profile',
        element: (
          <RoleBasedGuard roles={[Roles.CHANNEL_PARTNER]}>
            <ChannelPartnerProfile />
          </RoleBasedGuard>
        )
      },
      {
        path: '/my-profile-dealer',
        element: (
          <RoleBasedGuard roles={[Roles.DEALER]}>
            <DealerProfile />
          </RoleBasedGuard>
        )
      },
      {
        path: '/repair-request/view/:id',
        element: (
          <RoleBasedGuard roles={[Roles.WEB_ADMIN, Roles.CHANNEL_PARTNER, Roles.TECHNICIAN]}>
            <RepairRequestView />
          </RoleBasedGuard>
        )
      },
      {
        path: '/repair-request/view-only/:id',
        element: (
          <RoleBasedGuard roles={[Roles.DEALER]}>
            <RepairRequestDealerView />
          </RoleBasedGuard>
        )
      },
      {
        path: '/ewaste-request/view/:id',
        element: (
          <RoleBasedGuard roles={[Roles.WEB_ADMIN, Roles.CHANNEL_PARTNER, Roles.TECHNICIAN]}>
            <EWasteRequestView />
          </RoleBasedGuard>
        )
      },
      {
        path: '/book-request',
        element: (
          <RoleNActiveStatBasedGuard roles={[Roles.WEB_ADMIN, Roles.DEALER]}>
            <BookRequest />
          </RoleNActiveStatBasedGuard>
        )
      },
      {
        path: 'account',
        element: <Account />,
        children: [
          {
            path: '/',
            element: <AccountGeneral />
          },
          {
            path: 'notifications',
            element: <AccountNotifications />
          },
          {
            path: 'team',
            element: <OrganizationTeam />
          }
        ]
      },
      {
        path: 'customers',
        children: [
          {
            path: '/search',
            element: (
              <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                <CustomersManagement />
              </RoleBasedGuard>
            )
          },
          {
            path: '/delete',
            element: (
              <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                <DeleteCustomersAccount />
              </RoleBasedGuard>
            )
          },
          {
            path: 'view',
            element: (
              <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                <CustomerView />
              </RoleBasedGuard>
            )
          },
          {
            path: 'view/:customerId',
            element: (
              <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                <CustomerView />
              </RoleBasedGuard>
            )
          }
        ]
      },
      {
        path: 'allapks',
        element: <AllApkList />,
      },
      {
        path: 'channel-partners',
        children: [
          {
            path: '/addNew',
            element: <ChannelPartnerAdd />
          },
          {
            path: '/add/continue/:id',
            element: <ChannelPartnerDetails />
          },
          {
            path: '/main',
            element: (
              <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                <ChannelPartners />
              </RoleBasedGuard>
            ),
            children: [
              {
                path: '/incomplete',
                element: <IncompleteChannelPartners />
              },
              {
                path: '/invited',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <InvitedChannelPartners />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/verified',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <VerifiedChannelPartners />
                  </RoleBasedGuard>
                )
              },
            ]
          },
          {
            path: '/invited/approve/:id',
            element: (
              <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                <ChannelPartnerApprove />
              </RoleBasedGuard>
            )
          },
          {
            path: '/invited/onboard/:id',
            element: (
              <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                <ChannelPartnersManagement />
              </RoleBasedGuard>
            )
          },
          {
            path: '/verified/:id',
            element: (
              <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                <VerifiedChannelPartnerOverview />
              </RoleBasedGuard>
            )
          },
          {
            path: 'management',
            element: (
              <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                <ChannelPartnersManagement />
              </RoleBasedGuard>
            ),
            children: [
              {
                path: '/',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <ChannelPartner />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/profile-details',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <ChannelPartnerProfileDetail />
                  </RoleBasedGuard>
                )
              }
            ]
          },
          {
            path: '/add',
            element: (
              <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                <AddChannelPartner />
              </RoleBasedGuard>
            )
          }
        ]
      },
      {
        path: 'dealers',
        children: [
          {
            path: '/',
            element: (
              <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                <Dealers />
              </RoleBasedGuard>
            ),
          },
          {
            path: '/:id',
            element: (
              <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                <DealerDetails />
              </RoleBasedGuard>
            )
          },
          {
            path: '/add',
            element: (
              <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                <DealerAdd />
              </RoleBasedGuard>
            )
          }
        ]
      },
      {
        path: 'technicians',
        children: [
          {
            path: '/addNew',
            element: <TechnicianAdd />
          },
          {
            path: '/addNew/:cpId',
            element: <TechnicianAdd />
          },
          {
            path: '/add/continue/:id',
            element: <TechnicianDetails />
          },
          {
            path: '/main',
            element: <Technicians />,
            children: [
              {
                path: '/pending',
                element: <PendingTechnicians />
              },
              {
                path: '/verified',
                element: <VerifiedTechnicians />
              },
              {
                path: '/incomplete',
                element: <InCompleteTechnicians />
              },
            ]
          },
          {
            path: '/pending/approve/:id',
            element: <ApproveTechnician />
          },
          {
            path: '/verified/:id',
            element: <VerifiedTechnicianView />
          },
          {
            path: 'management',
            element: <TechniciansManagement />,
            children: [
              {
                path: '/',
                element: <Technicians />
              },
              {
                path: '/profile-details',
                element: <TechnicianProfileDetail />
              }
            ]
          },
        ]
      },
      {
        path: 'masters',
        children: [
          {
            path: '/',
            element: (
              <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                <Masters />
              </RoleBasedGuard>)
          },
          {
            path: 'base',
            element: (
              <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                <BaseMasters />
              </RoleBasedGuard>
            ),
            children: [
              {
                path: '/category',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <Category />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/product',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <Product />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/brand',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <Brand />
                  </RoleBasedGuard>
                )
              }
            ]
          },
          {
            path: 'model',
            children: [
              {
                path: '/all',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <ModelMaster />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/view/:id',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <ModelView />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/edit/:id',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <EditModel />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/add',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN, Roles.CHANNEL_PARTNER]}>
                    <AddModel />
                  </RoleBasedGuard>
                )
              }
            ]
          },
          {
            path: 'spare-part',
            children: [
              {
                path: '/main',
                element: <SpartPartMaster />,
                children: [
                  {
                    path: '/all',
                    element: (
                      <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                        <SpartPartMasterAll />
                      </RoleBasedGuard>
                    ),
                  }
                ]
              },
              {
                path: '/generic',
                element: <SpartPartMaster />,
                children: [
                  {
                    path: '/all',
                    element: (
                      <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                        <GenericSpareParts />
                      </RoleBasedGuard>
                    ),
                  },
                ]
              },
              {
                path: '/find',
                element: <SpartPartMaster />,
                children: [
                  {
                    path: '/spare-part-find',
                    element: (
                      <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                        <SparePartFind />
                      </RoleBasedGuard>
                    ),
                  }
                ]
              },
              {
                path: '/generic/add',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <AddGenericSparePart />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/view/:id',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <SparePartView />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/generic/view/:id',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <GenericSparePartView />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/modelView/:modelId/:sparePartId',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <SparePartModelView />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/edit/:sparePartId',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <SparePartEdit />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/generic/edit/:id',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <GenericSparePartEdit />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/addSparePart',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN, Roles.CHANNEL_PARTNER]}>
                    <AddSparePart />
                  </RoleBasedGuard>
                ),
                children: [
                  {
                    path: '/add',
                    element: (
                      <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                        <AddNewSparePart />
                      </RoleBasedGuard>
                    ),
                  },
                  {
                    path: '/assignSparePart',
                    element: (
                      <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                        <SparePartAssign />
                      </RoleBasedGuard>
                    ),
                  }
                ]
              }
            ]
          },
          {
            path: 'price',
            children: [
              {
                path: '/service',
                element: <PriceMaster />,
                children: [
                  {
                    path: '/all',
                    element: (
                      <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                        <PriceService />
                      </RoleBasedGuard>
                    ),
                  },
                ]
              },
              {
                path: '/service/add',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <AddServicePrice />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/model/view/:id',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <PriceServiceModelView />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/service/view/:key',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <ServicePriceView />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/spare-part',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <PriceMaster />
                  </RoleBasedGuard>
                ),
                children: [
                  {
                    path: '/all',
                    element: (
                      (
                        <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                          <PriceSpartPart />
                        </RoleBasedGuard>
                      )
                    ),
                  },
                ]
              },
              {
                path: '/spare-part/view/:id',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <ViewSparePartPriceDetails />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/spare-part/add',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <AddNewSparePartPrice />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/add',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <AddPrice />
                  </RoleBasedGuard>
                )
              },
            ]
          },
          {
            path: 'issue',
            children: [
              {
                path: '/all',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <IssueMaster />
                  </RoleBasedGuard>
                ),
              },
              {
                path: '/view/:issueId',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <ViewIssue />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/edit/:issueId',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <EditIssue />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/product/edit/:productId',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <EditProductIssue />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/product/view/:productId',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <ViewProductIssue />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/add',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <AddAssignIssue />
                  </RoleBasedGuard>
                ),
                children: [
                  {
                    path: '/new',
                    element: (
                      <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                        <AddIssue />
                      </RoleBasedGuard>
                    )
                  },
                  {
                    path: '/assign',
                    element: (
                      <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                        <AssignIssue />
                      </RoleBasedGuard>
                    )
                  }
                ]
              }
            ]
          },
          {
            path: 'repairService',
            element: (
              <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                <RepairServiceMaster />
              </RoleBasedGuard>
            )
          },
          {
            path: 'general',
            element: (
              <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                <GeneralMaster />
              </RoleBasedGuard>
            ),
            children: [
              {
                path: '/service-type',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <ServiceType />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/gst',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <GST />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/uom',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <UnitOfMeasurement />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/pincode',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <Pincode />
                  </RoleBasedGuard>
                )
              },
              {
                path: '/rewardPoint',
                element: (
                  <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                    <RewardPoint />
                  </RoleBasedGuard>
                )
              }
            ]
          },
          {
            path: 'eWasteService',
            element: (
              <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
                <EWasteServiceMaster />
              </RoleBasedGuard>
            )
          },
        ]
      },
      {
        path: 'services',
        children: [
          {
            path: '/',
            element: <Services />
          },
          {
            path: 'repair-request',
            children: [
              {
                path: '/all',
                element: (
                  <RepairRequestService />
                ),
              },
              {
                path: '/add/:serviceId',
                element: (
                  <AddModelRepairService />
                ),
              }
            ]
          }
        ]
      },
      {
        path: 'payments',
        element: (
          <RoleBasedGuard roles={[Roles.WEB_ADMIN, Roles.CHANNEL_PARTNER]}>
            <Payments />
          </RoleBasedGuard>
        )
      },
      {
        path: 'reward-points',
        element: (
          <RoleBasedGuard roles={[Roles.WEB_ADMIN, Roles.CHANNEL_PARTNER]}>
            <RewardPoints />
          </RoleBasedGuard>
        )
      },
      {
        path: 'escalations',
        children: [
          {
            path: '/',
            element: <Escalations />
          },
          {
            path: '/:id',
            element: <EscalationView />
          },
          {
            path: 'enquiry/:id',
            element: <EnquiryView />
          }
        ]
      },
      {
        path: 'statistics',
        element: (
          <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
            <Statistics />
          </RoleBasedGuard>
        )
      },
      {
        path: 'ewaste-drives',
        element: (
          <RoleBasedGuard roles={[Roles.WEB_ADMIN]}>
            <EwasteDrives />
          </RoleBasedGuard>
        )
      },
      {
        path: '/settings',
        element: <Settings />
      },
      {
        path: '/about-us',
        element: <AboutUs />
      },
    ]
  },
  {
    path: '/coming-soon',
    element: <ComingSoon />
  },
  {
    path: '*',
    element: <NotFound />
  }
];

export default routes;
